import { RouteType } from "types/RouteType";
import { throwIfFailed } from "shared/dialogSubmit";
import type RootStore from "stores/RootStore";
import InternalContainersStore from "./InternalContainersStore";
import type InstanceActionsStore from "../InstanceActions/InstanceActionsStore";

/**
 * Infrastructure → Internal Containers: IntraSync's OWN containers (HQ itself,
 * agents, harness databases), operator-designated via the `mark-internal`
 * instance action from the Client Instances Unassigned section. This view
 * manages them apart from the client fleet, with the SAME action set the
 * Client Instances grid has: live status, update badges, one-click Update to
 * the latest image, Deploy a specific tag, Roll Back, Start/Stop/Restart, a
 * Console link, and — for rows whose container no longer exists ('missing') —
 * Remove. Release (returns the container to the Unassigned pool) is the one
 * action unique to this page.
 *
 * It previously COUNTED available updates and offered no way to apply one, so
 * updating HQ's own containers meant going to the host by hand.
 *
 * The difference from the fleet grid is not the actions, it is the stakes:
 * these are our own containers, so a stop takes an HQ service down rather than
 * a customer's. Every action therefore routes through the confirmation dialog.
 * (Its extra "this is IntraSync's own container" warning was removed on
 * 2026-09-30 once HQ could update itself: the container serving HQ is
 * protected server-side — a stop is refused, an update is handed to a helper
 * that restores the old HQ if the new one does not come up healthy.)
 */
const internalContainersRoute: RouteType[] = [
  {
    name: "Internal Containers",
    path: "/infrastructure/internal-containers",
    layout: "/app",
    viewportCard: {
      header: {
        enableBackButton: true,
        title: "Internal Containers",
        enableRefreshButton: true,
        component: () => import("./InternalContainers.Header"),
      },
      body: {
        component: () => import("./InternalContainers.Body"),
        grayBackground: true,
      },
    },
    group: "Infrastructure",
    searchAliases: ["internal containers", "our containers", "intrasync infrastructure"],
    requiresAuth: true,
    requiresPermission: false,
    onEnter: ({ rootStore }) => {
      const store = (
        rootStore as unknown as { internalContainersStore?: InternalContainersStore }
      ).internalContainersStore;
      void store?.load();
    },
    dialogs: [
      {
        name: "InstanceDeployDialog",
        title: "Deploy Image",
        component: () => import("../InstanceActions/dialogs/DeployDialog.Body"),
        cancelButton: true,
        height: 420,
        submitButtonText: "Deploy",
        onSubmit: async ({ rootStore }: { rootStore: RootStore }) => {
          const actions = rootStore.instanceActionsViewStore as InstanceActionsStore;
          const internal = (
            rootStore as unknown as { internalContainersStore?: InternalContainersStore }
          ).internalContainersStore;
          // A refused deploy throws: the dialog stays open with the reason
          // in its footer (shared/dialogSubmit).
          throwIfFailed(await actions.submitDeploy(), actions.deployError, "The image could not be deployed.");
          await internal?.reload();
        },
        onSubmitValidation: ({ rootStore }: { rootStore: RootStore }) => {
          const actions = rootStore.instanceActionsViewStore as InstanceActionsStore;
          return actions.isDeployValid && actions.canManage;
        },
      },
      {
        name: "InstanceRollbackDialog",
        title: "Roll Back Image",
        component: () =>
          import("../InstanceActions/dialogs/RollbackDialog.Body"),
        cancelButton: true,
        height: 520,
        submitButtonText: "Roll Back",
        onSubmit: async ({ rootStore }: { rootStore: RootStore }) => {
          const actions = rootStore.instanceActionsViewStore as InstanceActionsStore;
          const internal = (
            rootStore as unknown as { internalContainersStore?: InternalContainersStore }
          ).internalContainersStore;
          // A refused rollback throws: the dialog stays open with the reason
          // in its footer (shared/dialogSubmit).
          throwIfFailed(await actions.submitRollback(), actions.rollbackError, "The rollback could not be started.");
          await internal?.reload();
        },
        onSubmitValidation: ({ rootStore }: { rootStore: RootStore }) => {
          const actions = rootStore.instanceActionsViewStore as InstanceActionsStore;
          return actions.isRollbackValid && actions.canManage;
        },
      },
      {
        name: "InstanceConfirmActionDialog",
        title: "Confirm Action",
        component: () =>
          import("../InstanceActions/dialogs/ConfirmActionDialog.Body"),
        cancelButton: true,
        // Tall enough for the progress panel the body shows once Confirm is tapped.
        height: 440,
        submitButtonText: "Confirm",
        onSubmit: async ({ rootStore }: { rootStore: RootStore }) => {
          const actions = rootStore.instanceActionsViewStore as InstanceActionsStore;
          const internal = (
            rootStore as unknown as { internalContainersStore?: InternalContainersStore }
          ).internalContainersStore;
          // Every action on this page is confirmed, because every container
          // here is one of OURS -- a stop takes an HQ service down rather than
          // a customer's. Anything unrecognised is a no-op rather than a
          // mis-dispatch.
          await actions.runConfirmed(async (instance, action, progressToken) => {
            if (!internal) return false;
            if (action === "remove") return internal.remove(instance);
            if (action === "unmark-internal") return internal.release(instance);
            // Server-resolved: the API picks the digest, so it has its own
            // runner rather than the generic lifecycle transport. Mirrors the
            // Client Instances grid exactly.
            if (action === "update-to-latest") {
              const ok = await actions.updateToLatest(instance);
              if (ok) await internal.reload();
              return ok;
            }
            return internal.runLifecycleAction(instance, action, progressToken);
          });
        },
      },
    ],
  },
];

export default internalContainersRoute;
